import { useEffect, useMemo, useState } from "react";
import { useLocation, useParams } from "react-router";
import { useSelector } from "react-redux";
import { Briefcase, Headphones, Users, Wallet } from "lucide-react";
import PageShell from "../../components/dashboard/PageShell";
import BackNavLink from "../../components/dashboard/BackNavLink";
import FilteredDataTable from "../../components/dashboard/FilteredDataTable";
import StatusBadge from "../../components/dashboard/StatusBadge";
import StatCard from "../../components/dashboard/StatCard";
import StatCardGrid from "../../components/dashboard/StatCardGrid";
import { CUSTOMERS_FILTER_CONFIG } from "../../config/tableFilterConfigs";
import { USE_MOCK_AUTH } from "../../config/api";
import { MOCK_CASES, MOCK_CUSTOMERS, formatCurrency } from "../../data/mock/dashboardData";
import { getInstitutionById, getInstitutionCaseFilterName } from "../../data/institutionsStore";
import {
  fetchInstitutionCustomersPage,
  fetchInstitutionCustomersSummary,
  normalizeOpsCustomer,
} from "../../services/institutionWorkspaceApi";
import { linkWithReturn } from "../../utils/navigationReturn";
import { personNameColumns } from "../../utils/personName";
import { opsCasesPath } from "../../utils/opsCasesPath";
import { showError } from "../../utils/toast";

function enrichMockCustomer(customer, institutionName) {
  const cases = MOCK_CASES.filter(
    (item) => item.customer === customer.name && item.institution === institutionName
  );
  const primary = cases[0];
  return {
    ...customer,
    numericId: Number(String(customer.id).replace(/\D/g, "")) || null,
    caseId: primary?.id ?? "—",
    accountNumber: customer.accountNumber || customer.account_number || primary?.accountNumber || "",
    institution: institutionName,
    outstandingAmount: Math.max(0, customer.amountOwed - customer.amountPaid),
    assignedAgent: primary?.assignedAgent ?? null,
    caseStatus: primary?.status ?? customer.status,
    updatedAt: primary?.updatedAt ?? null,
  };
}

export default function InstitutionCustomersPage() {
  const { id } = useParams();
  const location = useLocation();
  const token = useSelector((state) => state.auth.token);
  const useLiveApi = Boolean(token) && !USE_MOCK_AUTH;
  const institution = getInstitutionById(id);
  const institutionName = institution ? getInstitutionCaseFilterName(institution) : "";

  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      let shown = false;
      try {
        const currentInstitution = getInstitutionById(id);
        const currentName = currentInstitution
          ? getInstitutionCaseFilterName(currentInstitution)
          : "";

        if (!useLiveApi) {
          const mockRows = MOCK_CUSTOMERS.map((customer) =>
            enrichMockCustomer(customer, currentName)
          ).filter(
            (row) =>
              row.institution === currentName &&
              MOCK_CASES.some(
                (item) => item.customer === row.name && item.institution === currentName
              )
          );
          if (!cancelled) {
            setRows(mockRows);
            setSummary(null);
          }
          return;
        }

        const mapRows = (list) =>
          (list || [])
            .map((row) => normalizeOpsCustomer(row, currentInstitution?.name || currentName))
            .filter(Boolean);

        const [firstPage, customerSummary] = await Promise.all([
          fetchInstitutionCustomersPage(token, id, 1, 50),
          fetchInstitutionCustomersSummary(token, id).catch(() => null),
        ]);
        if (cancelled) return;
        setRows(mapRows(firstPage.rows));
        shown = true;
        setSummary(customerSummary);
        setLoading(false);

        const lastPage = Number(firstPage.pagination?.last_page || 1);
        if (lastPage > 1) {
          const rest = await Promise.all(
            Array.from({ length: lastPage - 1 }, (_, index) =>
              fetchInstitutionCustomersPage(token, id, index + 2, 50)
            )
          );
          if (cancelled) return;
          setRows((current) => current.concat(rest.flatMap((page) => mapRows(page.rows))));
        }
      } catch (error) {
        if (!cancelled) {
          showError(error.message || "Could not load customers.");
          if (!shown) setRows([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [id, token, useLiveApi]);

  const columns = useMemo(
    () => [
      { key: "numericId", header: "ID", type: "id" },
      { key: "updatedAt", header: "Date & time", type: "datetime" },
      { key: "accountNumber", header: "Account", type: "id" },
      ...personNameColumns(),
      { key: "phone", header: "Mobile", type: "phone" },
      { key: "customer_number", header: "Customer no.", type: "id" },
      { key: "caseId", header: "Case ID", type: "id" },
      { key: "cases_count", header: "Cases", type: "number" },
      { key: "amountOwed", header: "Amount owed", type: "currency" },
      { key: "amountPaid", header: "Amount paid", type: "currency" },
      { key: "outstandingAmount", header: "Outstanding", type: "currency" },
      { key: "daysOverdue", header: "Days overdue", type: "number" },
      {
        key: "status",
        header: "Status",
        type: "badge",
        render: (row) => <StatusBadge status={row.status || row.caseStatus} />,
      },
      {
        key: "actions",
        header: "Actions",
        align: "center",
        headerClassName: "text-center",
        actions: (row) => [
          {
            key: "cases",
            label: "View collection cases",
            icon: Briefcase,
            tone: "primary",
            to: linkWithReturn(
              opsCasesPath({
                account: row.accountNumber || row.account_number,
                caseId: row.caseId || row.case_id,
              }),
              location
            ),
          },
          {
            key: "payments",
            label: "View payments",
            icon: Wallet,
            tone: "emerald",
            to: linkWithReturn(`/dashboard/payments?customer=${encodeURIComponent(row.name)}`, location),
          },
          {
            key: "contact",
            label: "Contact history",
            icon: Headphones,
            tone: "sky",
            to: linkWithReturn(
              `/dashboard/contact-centre?customer=${encodeURIComponent(row.name)}&tab=calls`,
              location
            ),
          },
        ],
      },
    ],
    [location]
  );

  return (
    <PageShell
      title={institution?.name ? `${institution.name} customers` : "Institution customers"}
      description="Debtors uploaded for this institution."
      actions={<BackNavLink fallback={`/dashboard/institutions/${id}`} fallbackLabel="Institution" />}
    >
      {loading ? (
        <div className="mb-3 rounded-xl border border-primary/[0.07] bg-primary/[0.02] px-4 py-3 text-xs text-primary/50">
          Loading customers…
        </div>
      ) : null}

      <StatCardGrid cols={4}>
        <StatCard label="Customers" value={summary?.customers ?? rows.length} icon={Users} tone="primary" />
        <StatCard
          label="Total owed"
          value={formatCurrency(summary?.total_owed ?? rows.reduce((sum, row) => sum + (row.amountOwed || 0), 0))}
          icon={Wallet}
          tone="accent"
        />
        <StatCard
          label="Paid"
          value={formatCurrency(summary?.total_paid ?? rows.reduce((sum, row) => sum + (row.amountPaid || 0), 0))}
          icon={Wallet}
          tone="emerald"
        />
        <StatCard
          label="Outstanding"
          value={formatCurrency(rows.reduce((sum, row) => sum + (row.outstandingAmount || 0), 0))}
          icon={Wallet}
          tone="sky"
        />
      </StatCardGrid>

      <FilteredDataTable
        data={rows}
        columns={columns}
        config={{
          ...CUSTOMERS_FILTER_CONFIG,
          title: "Customer filters",
          searchKeys: [
            "name",
            "phone",
            "accountNumber",
            "customer_number",
            "email",
            "ghana_card_number",
            "id",
            "caseId",
            "numericId",
            "debt_customer_slug",
            "region",
          ],
          searchPlaceholder: "Search name, account, phone, Ghana Card, case ID…",
          fields: [
            { key: "region", label: "Region", type: "multi", optionsFrom: "region", section: "KYC" },
            { key: "gender", label: "Gender", type: "multi", optionsFrom: "gender", section: "KYC" },
            { key: "status", label: "Status", type: "multi", optionsFrom: "status", section: "Status" },
            { key: "amountOwed", label: "Amount owed (₵)", type: "range", min: 0, max: 50000, section: "Amounts & ageing" },
            { key: "amountPaid", label: "Amount paid (₵)", type: "range", min: 0, max: 50000, section: "Amounts & ageing" },
            { key: "outstandingAmount", label: "Outstanding (₵)", type: "range", min: 0, max: 50000, section: "Amounts & ageing" },
            { key: "daysOverdue", label: "Days overdue", type: "range", min: 0, max: 365, section: "Amounts & ageing" },
          ],
          metrics: [],
        }}
        pageSize={10}
        emptyMessage="No customers found for this institution."
      />
    </PageShell>
  );
}
